/* eslint-disable @typescript-eslint/no-explicit-any */
export type TValidHTMLElement = HTMLElement | SVGElement;

export function $(expr: string, root?: Element) {
  return typeof expr === "string" ? (root || document).querySelector(expr) : expr || null;
}

export function createSVG<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, any> = {}) {
  const element = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const attr in attrs) {
    if (attr === "append_to") {
      const parent = attrs.append_to as HTMLElement;
      parent.appendChild(element);
    } else if (attr === "innerHTML") {
      element.innerHTML = attrs.innerHTML;
    } else if (attr === "clipPath") {
      element.setAttribute("clip-path", `url(#${attrs[attr]})`);
    } else {
      element.setAttribute(attr, attrs[attr]);
    }
  }

  return element;
}

$.on = <K extends keyof HTMLElementEventMap>(
  element: HTMLElement,
  event: K,
  callback: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
) => {
  $.bind(element, event, callback);
};

$.off = <K extends keyof HTMLElementEventMap>(
  element: HTMLElement,
  event: K,
  listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
) => {
  element.removeEventListener(event, listener);
};

$.bind = <K extends keyof HTMLElementEventMap>(
  element: HTMLElement,
  event: K,
  callback: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
) => {
  event.split(/\s+/).forEach(function (event) {
    element.addEventListener(event as K, callback);
  });
};

$.delegate = <K extends keyof HTMLElementEventMap>(
  element: HTMLElement,
  event: K,
  selector: string,
  callback: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
) => {
  element.addEventListener(event, function (e) {
    const delegatedTarget = (e.target as any).closest(selector);
    if (delegatedTarget) {
      (e as any).delegatedTarget = delegatedTarget;
      (callback as any).call(this, e, delegatedTarget);
    }
  });
};

$.closest = (selector: string, element?: HTMLElement | null): HTMLElement | null => {
  if (!element) return null;

  if (element.matches(selector)) {
    return element;
  }

  return $.closest(selector, element.parentNode as HTMLElement);
};

$.attr = (element: TValidHTMLElement, attr: string | object, value?: any | null) => {
  if (!value && typeof attr === "string") {
    return element.getAttribute(attr);
  }

  if (typeof attr === "object") {
    for (const key in attr) {
      $.attr(element, key, attr[key as keyof typeof attr]);
    }
    return;
  }

  element.setAttribute(attr, value);
};
